import React, { useMemo, useState } from "react";
import { Button, Dropdown, Input, Popover, Select, Table, Tag } from "antd";
import { MoreOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as dayjs from "dayjs";
import urls from "../../../../urls";
import { usePurchaseOrderShipments } from "../../../../data/hooks";
import { formatPriceByCurrency } from "../../../../functions/formatters";
import { ListCard, Num, PageHeader, RecordLink } from "../../../../components/shared-components/purchase";

const statusColors = {
  draft: "default",
  shipped: "blue",
  in_transit: "orange",
  received: "green",
  completed: "cyan",
  cancelled: "red",
};

const statusLabels = {
  draft: "Concept",
  shipped: "Verzonden",
  in_transit: "Onderweg",
  received: "Ontvangen",
  completed: "Afgerond",
  cancelled: "Geannuleerd",
};

const PurchaseOrderShipmentsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State declarations must come before useEffect hooks that use them
  const [supplierFilter, setSupplierFilter] = useState(searchParams.get("supplier") || null);
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "active");
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page")) || 1);

  // Load filters from localStorage on component mount
  React.useEffect(() => {
    try {
      const savedFilters = localStorage.getItem("purchaseOrderShipmentsFilters");
      if (savedFilters) {
        const filters = JSON.parse(savedFilters);
        if (filters.supplierFilter) setSupplierFilter(filters.supplierFilter);
        if (filters.statusFilter) setStatusFilter(filters.statusFilter);
        if (filters.currentPage) setCurrentPage(filters.currentPage);
      } else {
        // Default: hide cancelled and completed shipments
        setStatusFilter("active");
      }
    } catch (error) {
      console.error("Error initializing filters:", error);
      // Fallback to default values if localStorage fails
      setStatusFilter("active");
      setSupplierFilter(null);
      setCurrentPage(1);
    }
  }, []);

  // Save filters to localStorage whenever they change
  React.useEffect(() => {
    try {
      const filters = {
        supplierFilter,
        statusFilter,
        currentPage,
      };
      localStorage.setItem("purchaseOrderShipmentsFilters", JSON.stringify(filters));
    } catch (error) {
      console.warn("Failed to save filters to localStorage:", error);
      // Silently fail - filters will work but won't persist
    }
  }, [supplierFilter, statusFilter, currentPage]);

  // Use hook to fetch shipments
  const { shipments, loading } = usePurchaseOrderShipments();

  // Sync filters to URL
  const updateSearchParams = (updates) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    setSearchParams(params, { replace: true });
  };

  // Get unique suppliers for filter
  const suppliers = [
    ...new Map(shipments.map((s) => [s.supplierId, { id: s.supplierId, name: s.supplierName }])).values(),
  ];

  // Apply filters
  const filteredShipments = shipments.filter((s) => {
    if (supplierFilter && s.supplierId !== supplierFilter) return false;
    if (statusFilter && statusFilter !== "All") {
      if (statusFilter === "active") {
        // Hide cancelled and completed shipments
        if (s.status === "cancelled" || s.status === "completed") return false;
      } else {
        if (s.status !== statusFilter) return false;
      }
    }
    if (search.trim()) {
      // Zoek op zendingnummer, container én de gekoppelde ordernummers — dat zijn
      // de drie dingen waarmee iemand een zending in de praktijk terugzoekt.
      const needle = search.trim().toLowerCase();
      const haystack = [
        s.shipmentNumber,
        s.containerNumber,
        s.trackingCode,
        s.supplierName,
        ...(s.sourceOrders || []).map((so) => so.purchaseOrderNumber),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    return true;
  });

  // Kerncijfers voor de subtitel: laat zien waar aandacht nodig is voordat de
  // gebruiker de tabel in duikt.
  const summary = useMemo(() => {
    const drafts = shipments.filter((s) => s.status === "draft").length;
    const inTransit = shipments.filter((s) => s.status === "shipped" || s.status === "in_transit").length;
    return { total: shipments.length, drafts, inTransit };
  }, [shipments]);

  const columns = [
    {
      title: t("shipment.shipment"),
      dataIndex: "shipmentNumber",
      key: "shipmentNumber",
      render: (value, record) => <RecordLink to={`${urls.purchaseOrderShipmentDetail}${record.id}`}>{value}</RecordLink>,
    },
    {
      title: t("supplier.supplier"),
      dataIndex: "supplierName",
      key: "supplierName",
      render: (name) => name || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color={statusColors[status] || "default"}>{statusLabels[status] || status}</Tag>,
    },
    {
      title: t("order.purchase_orders"),
      key: "sourceOrders",
      render: (_, record) => {
        const orders = record.sourceOrders || [];
        if (!orders.length) return <span style={{ color: "rgba(0, 0, 0, 0.25)" }}>—</span>;
        const [first, ...rest] = orders;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <RecordLink to={`${urls.purchaseOrdersList}/${first.purchaseOrderId}`}>
              {first.purchaseOrderNumber}
            </RecordLink>
            {rest.length ? (
              // Een zending kan regels uit zeven orders bundelen. Alle nummers naast
              // elkaar maakte de rij onleesbaar; de rest zit achter "+ n".
              <Popover
                trigger="click"
                placement="bottomLeft"
                title={`${orders.length} inkooporders`}
                content={
                  <div style={{ display: "flex", flexDirection: "column", maxWidth: 220 }}>
                    {orders.map((so) => (
                      <RecordLink
                        key={so.purchaseOrderId}
                        to={`${urls.purchaseOrdersList}/${so.purchaseOrderId}`}
                        style={{ padding: "5px 0" }}
                      >
                        {so.purchaseOrderNumber}
                      </RecordLink>
                    ))}
                  </div>
                }
              >
                <Button size="small" onClick={(e) => e.stopPropagation()}>
                  + {rest.length}
                </Button>
              </Popover>
            ) : null}
          </div>
        );
      },
    },
    {
      title: t("total_products"),
      dataIndex: "totalProducts",
      key: "totalProducts",
      align: "right",
      render: (value) => <Num>{(value || 0).toLocaleString("nl-NL")}</Num>,
    },
    {
      title: t("total_price"),
      dataIndex: "totalPrice",
      key: "totalPrice",
      align: "right",
      render: (v, row) => <Num>{v ? formatPriceByCurrency(row.totalPrice, row.currency) : "-"}</Num>,
    },
    {
      title: t("date"),
      dataIndex: "createdAt",
      key: "createdAt",
      align: "right",
      render: (sec) =>
        sec ? (
          // Datum en tijd onder elkaar: de datum is waarop gesorteerd wordt, de
          // tijd is bijzaak maar helpt om zendingen van dezelfde dag te scheiden.
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
            <Num>{dayjs.unix(sec).format("DD-MM-YYYY")}</Num>
            <Num size={11} muted>
              {dayjs.unix(sec).format("HH:mm")}
            </Num>
          </div>
        ) : (
          "-"
        ),
    },
    {
      key: "actions",
      width: 44,
      render: (_, record) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "open", label: t("shipment.shipment") + " openen" },
              ...(record.sourceOrders || []).slice(0, 1).map((so) => ({
                key: so.purchaseOrderId,
                label: `Inkooporder ${so.purchaseOrderNumber}`,
              })),
            ],
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();
              if (key === "open") navigate(`${urls.purchaseOrderShipmentDetail}${record.id}`);
              else navigate(`${urls.purchaseOrdersList}/${key}`);
            },
          }}
        >
          <Button type="text" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t("purchase_order_shipments")}
        subtitle={`${summary.total} zendingen · ${summary.drafts} concept · ${summary.inTransit} onderweg`}
      />

      <ListCard
        filters={
          <>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: "rgba(0, 0, 0, 0.35)" }} />}
              placeholder="Zending, inkooporder of container"
              style={{ width: 260 }}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
                updateSearchParams({ q: e.target.value, page: null });
              }}
            />
            <Select
              allowClear
              placeholder={t("supplier.supplier")}
              style={{ minWidth: 200 }}
              value={supplierFilter}
              onChange={(v) => {
                setSupplierFilter(v);
                setCurrentPage(1);
                updateSearchParams({ supplier: v, page: null });
              }}
              options={suppliers
                .slice()
                .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                .map((s) => ({ value: s.id, label: s.name || s.id }))}
            />
            <Select
              allowClear
              placeholder="Status"
              style={{ minWidth: 240 }}
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v);
                setCurrentPage(1);
                updateSearchParams({ status: v, page: null });
              }}
              options={[
                { value: "active", label: "Actief (zonder geannuleerd/afgerond)" },
                ...Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
              ]}
            />
          </>
        }
        footnote="Ordernummers zijn links, geen tags: een tag zegt alleen iets over de status."
      >
        <Table
          loading={loading}
          dataSource={filteredShipments}
          columns={columns}
          scroll={{ x: true }}
          rowKey="id"
          pagination={{
            current: currentPage,
            showSizeChanger: false,
            onChange: (page) => {
              setCurrentPage(page);
              updateSearchParams({ page: String(page) });
            },
          }}
          onRow={(record) => ({
            onClick: () => navigate(`${urls.purchaseOrderShipmentDetail}${record.id}`),
            style: { cursor: "pointer" },
          })}
        />
      </ListCard>
    </>
  );
};

export default PurchaseOrderShipmentsList;
